<template>
  <WelfareShell active="welfare-mapping" subtitle="ขั้นที่ 2 — เลือกว่าปัญหาแต่ละข้อควรเสนอสวัสดิการอะไร (ปรับได้บ่อย เช่น ทุกต้นปี) ลากสวัสดิการจากด้านขวามาวางที่ปัญหา หรือเลือกปัญหาแล้วกด + ระบบบันทึกให้ทันที">
    <template #actions>
      <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="load"><RefreshCwIcon class="w-4 h-4 mr-1" /> โหลดใหม่</button>
      <button type="button" class="btn btn-primary bg-primary-btn border-primary-btn" @click="openWelfare(null)"><PlusIcon class="w-4 h-4 mr-1" /> เพิ่มสวัสดิการใหม่</button>
    </template>

    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      <button type="button" class="stat-card" style="--c:#CE3131" @click="setScope('problems')">
        <div class="stat-num">{{ stats.problems }}</div>
        <div class="text-sm text-gray-700">ปัญหาทั้งหมด</div>
      </button>
      <button type="button" class="stat-card" :class="{ 'stat-card--on': pScope === 'unmapped' }" style="--c:#F78B00" title="กดเพื่อดูเฉพาะรายการเหล่านี้" @click="setScope('unmapped')">
        <div class="stat-num">{{ stats.unmapped }}</div>
        <div class="text-sm text-gray-700">ปัญหาที่ไม่มีสวัสดิการใช้ได้วันนี้</div>
      </button>
      <button type="button" class="stat-card" :class="{ 'stat-card--on': pScope === 'expired' }" style="--c:#a12424" title="กดเพื่อดูเฉพาะรายการเหล่านี้" @click="setScope('expired')">
        <div class="stat-num">{{ stats.withExpired }}</div>
        <div class="text-sm text-gray-700">รายการที่มีสวัสดิการหมดอายุ</div>
      </button>
      <div class="stat-card" style="--c:#42B983;cursor:default">
        <div class="stat-num">{{ stats.usable }} <span class="text-base text-theme-37">/ {{ catalog.length }}</span></div>
        <div class="text-sm text-gray-700">สวัสดิการที่ใช้ได้วันนี้</div>
      </div>
    </div>

    <div v-if="loading" class="box p-10 text-center text-theme-37"><LoadingIcon icon="oval" class="w-8 h-8 mx-auto" /> กำลังโหลด…</div>
    <div v-else class="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
      <!-- LEFT: problems, grouped by the 5 dimensions -->
      <div class="xl:col-span-7 box p-4">
        <div class="flex flex-col md:flex-row md:items-center gap-2 mb-2">
          <div class="relative flex-1">
            <SearchIcon class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-theme-37" />
            <input v-model.trim="pq" type="text" class="form-control pl-9" placeholder="ค้นหาปัญหา">
          </div>
          <select v-model="pScope" class="form-select md:w-56" aria-label="แสดง">
            <option value="problems">เฉพาะปัญหา</option>
            <option value="all">ปัญหา + กฎแนะนำสวัสดิการ</option>
            <option value="unmapped">ไม่มีสวัสดิการใช้ได้วันนี้</option>
            <option value="expired">มีสวัสดิการหมดอายุ</option>
          </select>
          <select v-model="pLevel" class="form-select md:w-36" aria-label="ระดับ"><option value="">ทุกระดับ</option><option v-for="(l, k) in LEVEL_LABEL" :key="k" :value="k">{{ l }}</option></select>
        </div>
        <div class="flex flex-wrap gap-1.5 mb-3">
          <button type="button" class="chip" :style="chipStyle(!pDim)" @click="pDim = ''">ทุกมิติ {{ problems.length }}</button>
          <button v-for="d in DIMENSIONS" :key="d" type="button" class="chip" :style="chipStyle(pDim === d, d)" @click="pDim = pDim === d ? '' : d">{{ d }} {{ dimCount[d] || 0 }}</button>
        </div>

        <div ref="problemsEl" class="flex flex-col gap-2 max-h-[72vh] overflow-y-auto pr-1">
          <div v-for="g in groups" :key="g.dim" class="flex flex-col gap-2">
            <div class="dim-head" :style="{ color: (DIMENSION_STYLE[g.dim] || {}).color || '#728096', background: (DIMENSION_STYLE[g.dim] || {}).background || '#f1f5f8' }">{{ g.dim === '-' ? 'ยังไม่ระบุมิติ' : 'มิติ' + g.dim }} <span class="font-normal">· {{ g.items.length }} รายการ</span></div>
            <div v-for="r in g.items" :key="r.rule_id" :data-rule-card="r.rule_id" class="rounded-md border bg-white p-3 transition-colors" :class="selectedId === r.rule_id ? 'border-primary-1 ring-1 ring-primary-1' : 'border-gray-200'" :style="{ borderLeft: '3px solid ' + (isProblemRule(r) ? (SEVERITY_COLOR[r.severity] || SEVERITY_COLOR.monitor) : '#6B85A4') }">
              <div class="flex items-start gap-2 cursor-pointer" @click="select(r)">
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-800 break-words">{{ ruleTitle(r) }}</div>
                  <div class="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-1">
                    <span class="chip chip-muted">{{ LEVEL_LABEL[r.level] }}</span>
                    <span v-if="isProblemRule(r)" class="chip" :style="SEVERITY_STYLE[r.severity] || SEVERITY_STYLE.monitor">{{ SEVERITY_LABEL[r.severity] || SEVERITY_LABEL.monitor }}</span>
                    <span v-else class="chip chip-outline">แนะนำสวัสดิการ</span>
                    <span v-if="r.enabled === false" class="chip chip-muted">ปิดใช้งาน</span>
                    <span class="text-xs text-theme-37">{{ r.rule_id }}</span>
                  </div>
                  <div v-if="conditionSentence(r, qByCode)" class="text-xs text-theme-37 mt-1 clamp-2 break-words" :title="conditionSentence(r, qByCode)">เมื่อ: {{ conditionSentence(r, qByCode) }}</div>
                </div>
                <span v-if="selectedId === r.rule_id" class="chip flex-shrink-0" style="color:#fff;background:#415A77">กำลังเลือก</span>
                <router-link :to="{ name: 'welfare-rules', query: { edit: r.rule_id } }" class="icon-btn icon-btn-plain flex-shrink-0" title="แก้ไขปัญหา / เงื่อนไข (ขั้นที่ 1)" @click.stop><Edit2Icon class="w-3.5 h-3.5" /></router-link>
              </div>
              <div class="mt-2 rounded-md border border-dashed p-2 min-h-[2.6rem] flex flex-wrap gap-1.5 items-center" :class="dragOverId === r.rule_id ? 'border-primary-1 bg-primary-main/40' : 'border-gray-300 bg-gray-50'" :data-rule="r.rule_id" :ref="(el) => setListEl(r.rule_id, el)">
                <span v-for="w in welfareOf(r)" :key="w.welfare_id" class="chip chip-outline bg-white" :class="{ 'opacity-60': w.active === false }" :style="chipPeriodStyle(w)" :data-id="w.welfare_id" :title="welfareTip(w)">
                  <span class="drag-handle -ml-1"><MoreVerticalIcon class="w-3 h-3" /></span>
                  <span class="max-w-[14rem] truncate">{{ w.name }}</span>
                  <span v-if="!isUsableToday(w.period)" class="font-normal">· {{ PERIOD_STATUS_LABEL[periodStatus(w.period)] }}</span>
                  <span v-else-if="w.active === false" class="font-normal">· ปิดใช้งาน</span>
                  <button type="button" class="ml-0.5 text-theme-37 hover:text-theme-24 no-drag" title="เอาออก" @click.stop="unlink(r, w.welfare_id)"><XIcon class="w-3 h-3" /></button>
                </span>
                <span v-if="!welfareOf(r).length" class="text-xs text-theme-37 no-drag" data-placeholder>ลากสวัสดิการมาวางที่นี่</span>
              </div>
              <button v-if="expiredOf(r).length" type="button" class="text-xs underline mt-1.5" style="color:#CE3131" @click="dropExpired(r)">เอาสวัสดิการที่หมดอายุออก ({{ expiredOf(r).length }})</button>
            </div>
          </div>
          <div v-if="!problems.length" class="empty-state">ไม่พบรายการที่ตรงกับตัวกรอง</div>
        </div>
      </div>

      <!-- RIGHT: welfare catalog -->
      <div class="xl:col-span-5 box p-4 xl:sticky xl:top-4">
        <div class="flex items-center gap-2 mb-2">
          <GiftIcon class="w-4 h-4" style="color:#1f8f5b" />
          <div class="text-sm font-semibold text-gray-800">สวัสดิการ ({{ welfareFiltered.length }})</div>
          <router-link :to="{ name: 'welfare-catalog' }" class="ml-auto text-xs" style="color:#415A77">จัดการรายการทั้งหมด →</router-link>
        </div>
        <div class="grid grid-cols-2 gap-2 mb-2">
          <select v-model="wPeriod" class="form-select" aria-label="ช่วงเวลาที่ใช้"><option v-for="o in periodOptions" :key="o.value" :value="o.value">{{ o.label }}</option></select>
          <select v-model="wDim" class="form-select" aria-label="มิติ"><option value="">ทุกมิติ</option><option v-for="d in DIMENSIONS" :key="d" :value="d">{{ d }}</option></select>
          <div class="relative col-span-2">
            <SearchIcon class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-theme-37" />
            <input v-model.trim="wq" type="text" class="form-control pl-9" placeholder="ค้นหาสวัสดิการ หน่วยงาน">
          </div>
        </div>
        <div class="text-xs text-theme-37 mb-2">
          <template v-if="selected">กด + เพื่อเพิ่มให้ <span class="font-medium text-gray-800">{{ ruleTitle(selected) }}</span></template>
          <template v-else>คลิกที่ปัญหาด้านซ้ายเพื่อเลือก แล้วกด + หรือลากการ์ดไปวางที่ปัญหาได้เลย</template>
        </div>
        <div ref="catalogEl" class="flex flex-col gap-1.5 max-h-[60vh] overflow-y-auto pr-1">
          <div v-for="w in welfareFiltered" :key="w.welfare_id" class="welfare-card flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5" :class="{ 'opacity-60': w.active === false || !isUsableToday(w.period), 'border-primary-1': selected && selectedHas(w.welfare_id) }" :data-id="w.welfare_id">
            <span class="drag-handle flex-shrink-0"><MoreVerticalIcon class="w-4 h-4 -mr-2" /><MoreVerticalIcon class="w-4 h-4" /></span>
            <div class="flex-1 min-w-0">
              <div class="text-sm text-gray-800 break-words leading-snug">{{ w.name }}</div>
              <div class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-theme-37 mt-0.5">
                <PeriodChip :period="w.period" />
                <span v-if="w.agency" class="break-words">{{ w.agency }}</span>
                <span v-if="w.usage && w.usage.rule_ids.length">· ใช้ใน {{ w.usage.rule_ids.length }} รายการ</span>
              </div>
            </div>
            <button type="button" class="icon-btn icon-btn-plain no-drag flex-shrink-0" title="แก้ไขสวัสดิการ / ปีที่ใช้" @click="openWelfare(w)"><Edit2Icon class="w-3.5 h-3.5" /></button>
            <button type="button" class="icon-btn no-drag flex-shrink-0" :class="selected && selectedHas(w.welfare_id) ? 'icon-btn-del' : 'icon-btn-plain'" :disabled="!selected" :title="selected ? (selectedHas(w.welfare_id) ? 'เอาออกจากปัญหาที่เลือก' : 'เพิ่มให้ปัญหาที่เลือก') : 'เลือกปัญหาก่อน'" @click="toggleForSelected(w)">
              <MinusIcon v-if="selected && selectedHas(w.welfare_id)" class="w-4 h-4" /><PlusIcon v-else class="w-4 h-4" />
            </button>
          </div>
          <div v-if="!welfareFiltered.length" class="empty-state">
            ไม่พบสวัสดิการ<span v-if="wPeriod"> ในช่วงเวลานี้ — <button type="button" class="underline" @click="wPeriod = ''">ดูทุกช่วงเวลา</button></span>
          </div>
        </div>
      </div>
    </div>

    <CatalogForm :show="showWelfare" :item="editingWelfare" @close="showWelfare = false" @saved="onWelfareSaved" />
  </WelfareShell>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import Sortable from "sortablejs";
import WelfareShell from "../components/WelfareShell.vue";
import PeriodChip from "../components/PeriodChip.vue";
import CatalogForm from "../catalog/CatalogForm.vue";
import { welfareApi, apiErrorMessage } from "../api";
import { DIMENSIONS, DIMENSION_STYLE, LEVEL_LABEL, SEVERITY_LABEL, SEVERITY_STYLE, SEVERITY_COLOR, isProblemRule } from "../constants";
import { ruleTitle, conditionSentence } from "../ruleText";
import { isUsableToday, periodStatus, periodLabel, periodFilterOptions, matchPeriodFilter, PERIOD_STATUS_LABEL, PERIOD_STATUS_STYLE } from "../period";
import { moveItem, revertDom } from "../useSortable";
import { toastSuccess, toastError } from "../components/useToast";

const route = useRoute();
const router = useRouter();
const rules = ref([]);
const catalog = ref([]);
const questions = ref([]);
const loading = ref(false);
const pq = ref(""); const pLevel = ref(""); const pDim = ref(""); const pScope = ref("problems");
const wq = ref(""); const wDim = ref(""); const wPeriod = ref("current");
const selectedId = ref(null);
const dragOverId = ref(null);
const catalogEl = ref(null);
const problemsEl = ref(null);
const showWelfare = ref(false);
const editingWelfare = ref(null);
const listEls = new Map();
const sortables = new Map();
const setListEl = (id, el) => { if (el) listEls.set(id, el); else listEls.delete(id); };

const qByCode = computed(() => Object.fromEntries(questions.value.map((x) => [x.code, x])));
const byId = computed(() => Object.fromEntries(catalog.value.map((c) => [String(c.welfare_id), c])));
const idsOf = (r) => (r.then?.welfare_ids || []).map(String);
/** the catalog is the source of truth for name / period / active, so a welfare edited here is reflected at once */
const welfareOf = (r) => idsOf(r).map((id) => byId.value[id] || { welfare_id: id, name: `สวัสดิการ #${id}` });
const usableOf = (r) => welfareOf(r).filter((w) => w.active !== false && isUsableToday(w.period));
const expiredOf = (r) => welfareOf(r).filter((w) => periodStatus(w.period) === "expired");

const chipStyle = (on, d) => {
  const s = d ? DIMENSION_STYLE[d] : { color: "#415A77", background: "#eef1f6" };
  return on ? { ...s, border: `1px solid ${s.color}`, cursor: "pointer" } : { color: "#728096", background: "#fff", border: "1px solid #e2e8f0", cursor: "pointer" };
};
const chipPeriodStyle = (w) => (isUsableToday(w.period) ? {} : PERIOD_STATUS_STYLE[periodStatus(w.period)]);
const welfareTip = (w) => [w.name, w.agency, periodLabel(w.period)].filter(Boolean).join("\n");

const stats = computed(() => {
  const probs = rules.value.filter(isProblemRule);
  return {
    problems: probs.length,
    unmapped: probs.filter((r) => !usableOf(r).length).length,
    withExpired: rules.value.filter((r) => expiredOf(r).length).length,
    usable: catalog.value.filter((w) => w.active !== false && isUsableToday(w.period)).length,
  };
});
const inScope = (r) => {
  if (pScope.value === "problems") return isProblemRule(r);
  if (pScope.value === "unmapped") return !usableOf(r).length;
  if (pScope.value === "expired") return expiredOf(r).length > 0;
  return true;
};
const scoped = computed(() => {
  const s = pq.value.toLowerCase();
  return rules.value.filter((r) => inScope(r) && (!pLevel.value || r.level === pLevel.value) && (!s || `${r.rule_id} ${r.name_th}`.toLowerCase().includes(s)));
});
const dimCount = computed(() => scoped.value.reduce((acc, r) => { if (r.dimension) acc[r.dimension] = (acc[r.dimension] || 0) + 1; return acc; }, {}));
const problems = computed(() => scoped.value.filter((r) => !pDim.value || r.dimension === pDim.value));
const groups = computed(() => [...DIMENSIONS, "-"]
  .map((dim) => ({ dim, items: problems.value.filter((r) => (r.dimension || "-") === dim) }))
  .filter((g) => g.items.length));

const periodOptions = computed(() => periodFilterOptions(catalog.value));
const welfareFiltered = computed(() => {
  const s = wq.value.toLowerCase();
  return catalog.value.filter((w) => {
    if (!matchPeriodFilter(w.period, wPeriod.value)) return false;
    if (wDim.value && !(w.dimensions || []).includes(wDim.value)) return false;
    if (s && !`${w.welfare_id} ${w.name || ""} ${w.agency || ""}`.toLowerCase().includes(s)) return false;
    return true;
  });
});
const selected = computed(() => rules.value.find((r) => r.rule_id === selectedId.value) || null);
const selectedHas = (wid) => !!selected.value && idsOf(selected.value).includes(String(wid));
const select = (r) => { selectedId.value = selectedId.value === r.rule_id ? null : r.rule_id; };
const setScope = (v) => { pScope.value = pScope.value === v && v !== "problems" ? "problems" : v; };

async function load() {
  loading.value = true;
  try {
    const [r, c, qs] = await Promise.all([welfareApi.rules.list(), welfareApi.catalog.list(), questions.value.length ? { items: questions.value } : welfareApi.questions().catch(() => ({ items: [] }))]);
    rules.value = r.items || [];
    catalog.value = c.items || [];
    questions.value = qs.items || [];
  } catch (e) {
    toastError(apiErrorMessage(e, "โหลดข้อมูลไม่สำเร็จ"));
  } finally {
    loading.value = false;
    await nextTick();
    attachAll();
  }
}

// ---- persistence ----
async function persist(rule, welfareIds, successText) {
  const before = idsOf(rule);
  rule.then = { ...(rule.then || {}), welfare_ids: welfareIds };
  try {
    const res = await welfareApi.rules.setWelfare(rule.rule_id, welfareIds);
    if (res.item) rule.then = res.item.then;
    refreshUsage();
    toastSuccess(successText);
  } catch (e) {
    rule.then = { ...(rule.then || {}), welfare_ids: before };
    toastError(apiErrorMessage(e));
  }
}
function refreshUsage() {
  const usage = {};
  for (const r of rules.value) for (const w of idsOf(r)) { (usage[w] = usage[w] || { rule_ids: [], problem_rule_ids: [] }).rule_ids.push(r.rule_id); if (isProblemRule(r)) usage[w].problem_rule_ids.push(r.rule_id); }
  for (const c of catalog.value) c.usage = usage[String(c.welfare_id)] || { rule_ids: [], problem_rule_ids: [] };
}
const link = (rule, wid) => {
  const ids = idsOf(rule);
  if (ids.includes(String(wid))) { toastSuccess("มีสวัสดิการนี้อยู่แล้ว"); return; }
  return persist(rule, [...ids, String(wid)], `เพิ่ม "${(byId.value[wid] || {}).name || wid}" แล้ว`);
};
const unlink = (rule, wid) => persist(rule, idsOf(rule).filter((x) => x !== String(wid)), "เอาสวัสดิการออกแล้ว");
const toggleForSelected = (w) => { if (!selected.value) return; selectedHas(w.welfare_id) ? unlink(selected.value, w.welfare_id) : link(selected.value, w.welfare_id); };
const dropExpired = (rule) => {
  const gone = new Set(expiredOf(rule).map((w) => String(w.welfare_id)));
  return persist(rule, idsOf(rule).filter((id) => !gone.has(id)), `เอาสวัสดิการที่หมดอายุออก ${gone.size} รายการแล้ว`);
};

// ---- add / edit a welfare (name, year…) without leaving the board ----
const openWelfare = (w) => { editingWelfare.value = w; showWelfare.value = true; };
async function onWelfareSaved(item) {
  const wasNew = !editingWelfare.value;
  showWelfare.value = false;
  try {
    const c = await welfareApi.catalog.list();
    catalog.value = c.items || [];
  } catch (e) {
    toastError(apiErrorMessage(e, "โหลดรายการสวัสดิการไม่สำเร็จ"));
  }
  // bring the new welfare into view so it can be dropped on a problem straight away
  if (wasNew && item) { wq.value = item.name || ""; wDim.value = ""; if (!matchPeriodFilter(item.period, wPeriod.value)) wPeriod.value = ""; }
}

// ---- drag & drop: catalog cards (clone) -> problem lists; reorder inside a problem list; copy between problems ----
let catalogSortable = null;
function attachAll() {
  if (catalogEl.value && !catalogSortable) {
    catalogSortable = Sortable.create(catalogEl.value, {
      group: { name: "welfare", pull: "clone", put: false }, sort: false, handle: ".drag-handle", filter: ".no-drag",
      animation: 150, ghostClass: "sortable-ghost", chosenClass: "sortable-chosen", forceFallback: true, fallbackOnBody: true, fallbackTolerance: 3,
      onMove: (evt) => { dragOverId.value = evt.to && evt.to.dataset ? evt.to.dataset.rule || null : null; },
      onEnd: () => { dragOverId.value = null; },
    });
  }
  // drop instances whose element left the DOM (filtered-out problems)
  for (const [el, s] of [...sortables]) {
    if (!el.isConnected) { try { s.destroy(); } catch (e) { /* noop */ } sortables.delete(el); }
  }
  for (const [id, el] of listEls) {
    if (sortables.has(el)) continue;
    const s = Sortable.create(el, {
      group: { name: "welfare", pull: "clone", put: true }, handle: ".drag-handle", filter: ".no-drag,[data-placeholder]", draggable: "[data-id]",
      animation: 150, ghostClass: "sortable-ghost", chosenClass: "sortable-chosen", forceFallback: true, fallbackOnBody: true, fallbackTolerance: 3,
      onMove: (evt) => { dragOverId.value = evt.to && evt.to.dataset ? evt.to.dataset.rule || null : null; },
      onAdd: (evt) => {
        const wid = evt.item && evt.item.dataset.id;
        if (evt.item && evt.item.parentNode) evt.item.parentNode.removeChild(evt.item);   // Vue renders the chip from state
        dragOverId.value = null;
        const rule = rules.value.find((r) => r.rule_id === id);
        if (rule && wid) link(rule, wid);
      },
      onUpdate: (evt) => {
        revertDom(evt);
        const rule = rules.value.find((r) => r.rule_id === id);
        if (!rule || evt.oldIndex === evt.newIndex) return;
        const ids = idsOf(rule);
        moveItem(ids, evt.oldIndex, evt.newIndex);
        persist(rule, ids, "บันทึกลำดับสวัสดิการแล้ว");
      },
      onEnd: () => { dragOverId.value = null; },
    });
    sortables.set(el, s);
  }
}
function destroyAll() {
  if (catalogSortable) { try { catalogSortable.destroy(); } catch (e) { /* noop */ } catalogSortable = null; }
  for (const s of sortables.values()) { try { s.destroy(); } catch (e) { /* noop */ } }
  sortables.clear();
}
watch(problems, async () => { await nextTick(); attachAll(); });

onMounted(async () => {
  await load();
  // deep link from the rules page: /admin/welfare/mapping?rule=HOUSE-R003 selects that problem
  const id = route.query.rule;
  if (!id) return;
  const r = rules.value.find((x) => x.rule_id === id);
  router.replace({ query: {} });
  if (!r) return;
  if (!isProblemRule(r)) pScope.value = "all";
  selectedId.value = r.rule_id;
  await nextTick();
  const card = problemsEl.value && problemsEl.value.querySelector(`[data-rule-card="${String(id).replace(/"/g, "")}"]`);
  if (card) card.scrollIntoView({ block: "center" });
  attachAll();
});
onBeforeUnmount(destroyAll);
</script>

<style scoped>
.welfare-card { cursor: default; }
.welfare-card .drag-handle { cursor: grab; }
.stat-card { text-align: left; border-radius: 0.375rem; border: 1px solid #e2e8f0; border-top: 3px solid var(--c); background: #fff; padding: 0.75rem; transition: box-shadow 0.12s ease; }
button.stat-card:hover { box-shadow: 0 2px 8px rgba(65, 90, 119, 0.15); }
.stat-card--on { box-shadow: inset 0 0 0 1px var(--c); background: #fffaf3; }
.stat-num { font-size: 1.5rem; font-weight: 600; line-height: 2rem; color: var(--c); }
.dim-head { position: sticky; top: 0; z-index: 5; font-size: 0.8rem; font-weight: 600; padding: 0.3rem 0.7rem; border-radius: 0.375rem; }
</style>
